import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MapPin, Mail, Instagram, Facebook } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_9c04a317-074b-47df-afd3-88339795c9c4/artifacts/rv087trj_logo.jpg";

const CONTACT_DEFAULTS = {
  address: 'Str. Henri Coandă 33A, Brașov, România',
  phone: '0761 961 789',
  email: 'vanzari@shagunsurfaces.ro',
  whatsapp: '40761961789',
  instagram: 'https://www.instagram.com/shagunsurfaces/',
  facebook: 'https://www.facebook.com/profile.php?id=61574700722525',
};

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [contact, setContact] = useState(CONTACT_DEFAULTS);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/settings`).then(r => {
      if (r.data && Object.keys(r.data).length > 0) {
        setContact(prev => ({ ...prev, ...r.data }));
      }
    }).catch(() => {});
  }, []);

  const navLinks = [
    { href: '/', label: 'Acasă' },
    { href: '/placi-ceramice', label: 'Plăci Ceramice' },
    { href: '/parchet', label: 'Parchet' },
    { href: '/riflaje', label: 'Riflaje' },
    { href: '/obiecte-sanitare', label: 'Obiecte Sanitare' },
    { href: '/configurator-baie', label: 'Configurator Baie' },
    { href: '/contact', label: 'Contact' },
  ];

  const isActive = (href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header 
        className={`fixed w-full top-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-[#050505]/95 backdrop-blur-xl border-b border-white/10' : 'bg-transparent'
        }`}
        data-testid="main-header"
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <img src={contact.logo_url ? (contact.logo_url.startsWith('/api') ? `${API_URL}${contact.logo_url}` : contact.logo_url) : LOGO_URL} alt="Shagun Surfaces" className="h-10 w-auto rounded-sm" />
              <span className="hidden sm:block font-serif text-lg text-white tracking-wide">
                SHAGUN <span className="text-[#D4AF37]">SURFACES</span>
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`nav-link text-sm font-medium tracking-wide ${
                    isActive(link.href) ? 'text-[#D4AF37] active' : 'text-[#A3A3A3]'
                  }`}
                  data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden text-white p-2"
              data-testid="mobile-menu-btn"
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden bg-[#121212] border-t border-white/10" data-testid="mobile-nav">
            <nav className="max-w-7xl mx-auto px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`text-base font-medium py-2 ${
                    isActive(link.href) ? 'text-[#D4AF37]' : 'text-[#A3A3A3]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main>{children}</main>

      {/* WhatsApp Button */}
      <a
        href={`https://wa.me/${contact.whatsapp}`}
        target="_blank"
        rel="noopener noreferrer"
        className="whatsapp-btn"
        data-testid="whatsapp-btn"
      >
        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] border-t border-white/5" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Company Info */}
            <div>
              <img src={LOGO_URL} alt="Shagun Surfaces" className="h-16 w-auto mb-6" />
              <p className="text-[#A3A3A3] text-sm leading-relaxed">
                Transformăm spațiile în opere de artă cu materiale premium pentru finisaje de excepție.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-serif text-xl mb-6">Navigare</h4>
              <ul className="space-y-3">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <Link 
                      to={link.href} 
                      className="text-[#A3A3A3] hover:text-[#D4AF37] transition-colors text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-white font-serif text-xl mb-6">Contact</h4>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <MapPin className="text-[#D4AF37] flex-shrink-0 mt-1" size={18} />
                  <span className="text-[#A3A3A3] text-sm">
                    {contact.address}
                  </span>
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="text-[#D4AF37]" size={18} />
                  <a href={`tel:+${contact.whatsapp}`} className="text-[#A3A3A3] hover:text-[#D4AF37] text-sm">
                    {contact.phone}
                  </a>
                </li>
                <li className="flex items-center gap-3">
                  <Mail className="text-[#D4AF37]" size={18} />
                  <a href={`mailto:${contact.email}`} className="text-[#A3A3A3] hover:text-[#D4AF37] text-sm">
                    {contact.email}
                  </a>
                </li>
              </ul>
            </div>

            {/* Social & Delivery */}
            <div>
              <h4 className="text-white font-serif text-xl mb-6">Social Media</h4>
              <div className="flex gap-4 mb-8">
                {contact.instagram && (
                <a
                  href={contact.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#121212] border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                  data-testid="instagram-link"
                >
                  <Instagram size={18} />
                </a>
                )}
                {contact.facebook && (
                <a
                  href={contact.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 bg-[#121212] border border-white/10 flex items-center justify-center hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all"
                  data-testid="facebook-link"
                >
                  <Facebook size={18} />
                </a>
                )}
              </div>
              <div className="bg-[#121212] border border-white/10 p-4">
                <p className="text-[#D4AF37] font-medium text-sm mb-1">Livrare Națională</p>
                <p className="text-[#A3A3A3] text-xs">Livrăm în toată România</p>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="border-t border-white/5 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[#A3A3A3] text-sm">
              © {new Date().getFullYear()} Shagun Surfaces. Toate drepturile rezervate.
            </p>
            <Link 
              to="/admin/login" 
              className="text-[#A3A3A3] hover:text-[#D4AF37] text-sm"
              data-testid="admin-link"
            >
              Admin
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Layout;
